'use client';

import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { IconCheck, IconChevronDown } from '@tabler/icons-react';
import { cn } from '../lib/utils';
import { VariantProps, cva } from 'class-variance-authority';
import {
  buildCustomEvent,
  CustomEventMap,
  listenCustomEvent,
  stopListenCustomEvent,
} from '../lib/custom-event';

const variants = cva('', {
  variants: {
    variant: {
      filled:
        'flex h-10 w-full items-center justify-between rounded-md border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 bg-gray-100 ',
      underline:
        'flex flex-row space-between py-2.5 px-0 w-full text-sm text-gray-500 bg-transparent border-0 border-b-2 border-gray-200 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer',
      outline:
        'flex h-10 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
      outlineNoRings:
        'flex h-10 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
    },
    size: {
      xs: 'h-6 px-2 text-xs rounded',
      sm: 'h-8 px-3 text-sm',
      md: 'h-10 px-4',
      lg: 'h-12 px-6 text-lg',
    },
  },
  defaultVariants: {
    size: 'md',
    variant: 'outline',
  },
});

/**
 * Displays a list of options for the user to pick from—triggered by a button.
 */

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

// SelectContent renders in place. Wrap it in a portal when an ancestor clips or
// scrolls it, e.g. inside a dialog.
const SelectPortal = SelectPrimitive.Portal;

const Select = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root>
>(({ onOpenChange, ...props }, ref) => {
  const [open, setOpen] = React.useState(false);
  const id = React.useId();

  const changeOpen = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange],
  );

  // Radix Select ships its own dismissable layer, so inside a dialog opening
  // another select leaves this one open: it closes when told another opened.
  React.useEffect(() => {
    if (!open) return;

    const closeWhenAnotherOpens = (
      event: CustomEvent<CustomEventMap['select-open-change']>,
    ) => {
      if (event.detail.open && event.detail.id !== id) changeOpen(false);
    };

    listenCustomEvent('select-open-change', closeWhenAnotherOpens);

    return () =>
      stopListenCustomEvent('select-open-change', closeWhenAnotherOpens);
  }, [open, id, changeOpen]);

  return (
    <SelectPrimitive.Root
      {...props}
      ref={ref}
      open={open}
      onOpenChange={(nextOpen) => {
        changeOpen(nextOpen);

        window.dispatchEvent(
          buildCustomEvent('select-open-change', {
            open: nextOpen,
            id,
          }),
        );
      }}
    />
  );
});

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
    VariantProps<typeof variants> & {
      classNames?: {
        icon?: string;
      };
    }
>(({ className, children, classNames, ...props }, ref) => {
  if (props.variant === 'underline') {
    return (
      <SelectPrimitive.Trigger
        ref={ref}
        className={cn(
          variants({
            size: props.size,
            variant: props.variant,
          }),
          className,
        )}
        {...props}
      >
        <div className='flex flex-row justify-between w-full '>
          {children}
          <SelectPrimitive.Icon asChild>
            <IconChevronDown
              className={cn('h-4 w-4 opacity-50', classNames?.icon)}
            />
          </SelectPrimitive.Icon>
        </div>
      </SelectPrimitive.Trigger>
    );
  }

  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        variants({
          size: props.size,
          variant: props.variant,
        }),
        'min-w-0',
        className,
        'bg-white',
      )}
      {...props}
    >
      {/* Keep the selected value on a single line: truncate with an ellipsis
          instead of wrapping to two lines when it's long. */}
      <span className='flex-1 min-w-0 truncate text-left'>{children}</span>
      <SelectPrimitive.Icon asChild>
        <IconChevronDown className='h-4 w-4 shrink-0 opacity-50' />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

// Pixels a wheel line scrolls, for browsers that report the wheel in lines.
const WHEEL_LINE_PX = 16;

// The nearest list under the pointer that scrolls, e.g. a ScrollArea inside the
// options, else the select's own viewport.
const scrollableFrom = (target: EventTarget | null, viewport: HTMLElement) => {
  for (
    let element = target instanceof HTMLElement ? target : null;
    element && element !== viewport;
    element = element.parentElement
  ) {
    const { overflowY } = getComputedStyle(element);

    if (
      element.scrollHeight > element.clientHeight &&
      (overflowY === 'auto' || overflowY === 'scroll')
    )
      return element;
  }

  return viewport;
};

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => {
  // State, not a ref: closed, Radix renders the list elsewhere, so the
  // viewport that shows is a new node each time it opens.
  const [viewport, setViewport] = React.useState<HTMLDivElement | null>(null);

  // Inside a dialog its scroll lock, another copy than the select's, swallows
  // the wheel; only then the list scrolls by hand. The lock cancels it on the
  // document, after this listener, so it is read once the event is done.
  React.useEffect(() => {
    if (!viewport) return;

    const scrollIfSwallowed = (event: WheelEvent) => {
      const scrollable = scrollableFrom(event.target, viewport);
      const delta =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? event.deltaY * WHEEL_LINE_PX
          : event.deltaY;

      setTimeout(() => {
        if (event.defaultPrevented) scrollable.scrollTop += delta;
      });
    };

    viewport.addEventListener('wheel', scrollIfSwallowed, { passive: true });

    return () => viewport.removeEventListener('wheel', scrollIfSwallowed);
  }, [viewport]);

  return (
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]',
        className,
      )}
      position={position}
      {...props}
    >
      <SelectPrimitive.Viewport
        ref={setViewport}
        className={cn(
          'p-1',
          position === 'popper' &&
            'w-full min-w-[var(--radix-select-trigger-width)]',
          'SelectViewport',
          '!mt-0',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  );
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('py-1.5 pl-8 pr-2 text-sm font-semibold', className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

export const selectItemVariants = cva(
  'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
  {
    variants: {
      variant: {
        default: '',
      },
    },
  },
);

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(selectItemVariants(), className)}
    {...props}
  >
    <span className='absolute left-2 flex h-3.5 w-3.5 items-center justify-center'>
      <SelectPrimitive.ItemIndicator>
        <IconCheck className='h-4 w-4' />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectPortal,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
};
